import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Eye,
  Calendar,
  MapPin,
  Clock,
  Users,
  ChevronLeft,
  ChevronRight,
  GraduationCap,
  Briefcase,
  Building2,
} from "lucide-react";
import { toast } from "react-toastify";
import eventService from "../../../../../api/EventService";
import organizerService from "../../../../../api/OrganizerService";
import { EventActionsMenu, RescheduleEventModal, CancelEventModal, DeleteEventModal, MarkAsClosedModal } from "../../events/components";

// Helper function to get event title with truncation
const getEventTitle = (event, maxLength = 50) => {
  const title = event.event_title || event.venue_name || "Untitled Event";
  if (title.length > maxLength) {
    return title.substring(0, maxLength) + "...";
  }
  return title;
};

const ConferenceTradeMissionTable = ({
  events,
  onView,
  onEdit,
  onViewAnalytics,
  currentPage,
  itemsPerPage,
  totalCount,
  onPageChange,
  onItemsPerPageChange,
  viewMode = "card",
  onRefresh,
}) => {
  const navigate = useNavigate();
  const totalPages = Math.ceil(totalCount / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalCount);

  // Reschedule modal state
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState(false);
  const [eventToReschedule, setEventToReschedule] = useState(null);
  const [isRescheduling, setIsRescheduling] = useState(false);

  // Cancel modal state
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [eventToCancel, setEventToCancel] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);

  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Mark as Closed modal state
  const [isMarkAsClosedModalOpen, setIsMarkAsClosedModalOpen] = useState(false);
  const [eventToClose, setEventToClose] = useState(null);
  const [isClosing, setIsClosing] = useState(false);

  // Organizers lookup map
  const [organizersMap, setOrganizersMap] = useState({});

  // Fetch organizers on mount
  useEffect(() => {
    const fetchOrganizers = async () => {
      try {
        const organizersResponse = await organizerService.getOrganizers();
        if (organizersResponse.data?.status_code === 200) {
          const organizersList = Array.isArray(organizersResponse.data.data)
            ? organizersResponse.data.data
            : organizersResponse.data.data || [];
          
          // Create a map of organizer_slug -> organizer name
          const map = {};
          organizersList.forEach((org) => {
            if (org.slug && org.name) {
              map[org.slug] = org.name;
            }
          });
          setOrganizersMap(map);
        }
      } catch (error) {
        console.warn("Could not fetch organizers:", error);
      }
    };

    fetchOrganizers();
  }, []);

  // Helper function to get organizer name
  const getOrganizerName = (event) => {
    if (event.organizer_name) return event.organizer_name;
    if (event.organizer?.name) return event.organizer.name;
    if (event.organizer_slug && organizersMap[event.organizer_slug]) {
      return organizersMap[event.organizer_slug];
    }
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatTime = (timeString) => {
    if (!timeString) return "";
    return timeString;
  };

  // Format date for date input (YYYY-MM-DD)
  const formatDateForInput = (dateString) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch {
      return "";
    }
  };

  // Handle reschedule action
  const handleReschedule = (event) => {
    setEventToReschedule(event);
    setIsRescheduleModalOpen(true);
  };

  // Handle reschedule confirmation
  const handleRescheduleConfirm = async (data) => {
    if (!eventToReschedule) return;

    const eventSlug = eventToReschedule.slug || eventToReschedule.event_slug;
    if (!eventSlug) {
      toast.error("Event identifier not found");
      setIsRescheduleModalOpen(false);
      setEventToReschedule(null);
      return;
    }

    setIsRescheduling(true);
    try {
      const payload = {
        event_date: data.date,
        event_time: data.time,
      };

      const response = await eventService.rescheduleEvent(eventSlug, payload);
      if (response.status) {
        toast.success(
          response.message || 
          response.data?.reason || 
          "Event rescheduled successfully"
        );
        setIsRescheduleModalOpen(false);
        setEventToReschedule(null);
        if (onRefresh && typeof onRefresh === "function") {
          onRefresh();
        }
      } else {
        const errorMessage = 
          response.message || 
          response.data?.reason || 
          "Failed to reschedule event";
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = 
        error?.response?.data?.message ||
        error?.response?.data?.reason ||
        error.message || 
        "An error occurred while rescheduling event";
      toast.error(errorMessage);
    } finally {
      setIsRescheduling(false);
    }
  };

  // Handle cancel action
  const handleCancel = (event) => {
    setEventToCancel(event);
    setIsCancelModalOpen(true);
  };

  // Handle cancel confirmation
  const handleCancelConfirm = async () => {
    if (!eventToCancel) return;

    const eventSlug = eventToCancel.slug || eventToCancel.event_slug;
    if (!eventSlug) {
      toast.error("Event identifier not found");
      setIsCancelModalOpen(false);
      setEventToCancel(null);
      return;
    }

    setIsCancelling(true);
    try {
      const payload = {};

      const response = await eventService.cancelEvent(eventSlug, payload);
      if (response.status) {
        toast.success(
          response.message || 
          response.data?.reason || 
          "Event cancelled successfully"
        );
        setIsCancelModalOpen(false);
        setEventToCancel(null);
        if (onRefresh && typeof onRefresh === "function") {
          onRefresh();
        }
      } else {
        const errorMessage = 
          response.message || 
          response.data?.reason || 
          "Failed to cancel event";
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = 
        error?.response?.data?.message ||
        error?.response?.data?.reason ||
        error.message || 
        "An error occurred while cancelling event";
      toast.error(errorMessage);
    } finally {
      setIsCancelling(false);
    }
  };

  // Handle delete action
  const handleDelete = (event) => {
    setEventToDelete(event);
    setIsDeleteModalOpen(true);
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;

    const eventSlug = eventToDelete.slug || eventToDelete.event_slug;
    if (!eventSlug) {
      toast.error("Event identifier not found");
      setIsDeleteModalOpen(false);
      setEventToDelete(null);
      return;
    }

    setIsDeleting(true);
    try {
      const response = await eventService.deleteEvent(eventSlug);
      if (response.status) {
        toast.success(
          response.message || 
          response.data?.reason || 
          "Event deleted successfully"
        );
        setIsDeleteModalOpen(false);
        setEventToDelete(null);
        if (onRefresh && typeof onRefresh === "function") {
          onRefresh();
        }
      } else {
        const errorMessage = 
          response.message || 
          response.data?.reason || 
          "Failed to delete event";
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = 
        error?.response?.data?.message ||
        error?.response?.data?.reason ||
        error.message || 
        "An error occurred while deleting event";
      toast.error(errorMessage);
    } finally {
      setIsDeleting(false);
    }
  };

  // Handle mark as closed action
  const handleMarkAsClosed = (event) => {
    setEventToClose(event);
    setIsMarkAsClosedModalOpen(true);
  };

  // Handle mark as closed confirmation
  const handleMarkAsClosedConfirm = async () => {
    if (!eventToClose) return;

    const eventSlug = eventToClose.slug || eventToClose.event_slug;
    if (!eventSlug) {
      toast.error("Event identifier not found");
      setIsMarkAsClosedModalOpen(false);
      setEventToClose(null);
      return;
    }

    setIsClosing(true);
    try {
      const response = await eventService.markAsClosed(eventSlug);
      if (response.status) {
        toast.success(
          response.message || 
          response.data?.reason || 
          "Event marked as closed successfully"
        );
        setIsMarkAsClosedModalOpen(false);
        setEventToClose(null);
        // Refresh events list if callback provided
        if (onRefresh && typeof onRefresh === "function") {
          onRefresh();
        }
      } else {
        const errorMessage = 
          response.message || 
          response.data?.reason || 
          "Failed to mark event as closed";
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = 
        error?.response?.data?.reason ||
        error.message || 
        "An error occurred while marking event as closed";
      toast.error(errorMessage);
    } finally {
      setIsClosing(false);
    }
  };

  const getEventStatus = (event) => {
    const eventState = event.event_state || event.status;
    
    if (eventState) {
      const stateLower = eventState.toLowerCase();
      
      switch (stateLower) {
        case "cancelled":
          return { label: "Cancelled", color: "bg-red-100 text-red-700" };
        case "closed":
          return { label: "Closed", color: "bg-gray-100 text-gray-700" };
        case "active":
        case "scheduled":
          if (!event.event_date) {
            return { label: "Scheduled", color: "bg-blue-100 text-blue-700" };
          }
          const eventDate = new Date(event.event_date);
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          eventDate.setHours(0, 0, 0, 0);

          if (eventDate < today) {
            return { label: "Past", color: "bg-gray-100 text-gray-700" };
          } else if (eventDate.getTime() === today.getTime()) {
            return { label: "Today", color: "bg-green-100 text-green-700" };
          } else {
            const daysUntil = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
            if (daysUntil <= 7) {
              return { label: "Upcoming", color: "bg-orange-100 text-orange-700" };
            }
            return { label: "Scheduled", color: "bg-blue-100 text-blue-700" };
          }
        case "past":
          return { label: "Past", color: "bg-gray-100 text-gray-700" };
        default:
          return { 
            label: eventState.charAt(0).toUpperCase() + eventState.slice(1), 
            color: "bg-gray-100 text-gray-700" 
          };
      }
    }
    
    if (!event.event_date) {
      return { label: "Scheduled", color: "bg-blue-100 text-blue-700" };
    }
    const eventDate = new Date(event.event_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    eventDate.setHours(0, 0, 0, 0);

    if (eventDate < today) {
      return { label: "Past", color: "bg-gray-100 text-gray-700" };
    } else if (eventDate.getTime() === today.getTime()) {
      return { label: "Today", color: "bg-green-100 text-green-700" };
    } else {
      const daysUntil = Math.ceil((eventDate - today) / (1000 * 60 * 60 * 24));
      if (daysUntil <= 7) {
        return { label: "Upcoming", color: "bg-orange-100 text-orange-700" };
      }
      return { label: "Scheduled", color: "bg-blue-100 text-blue-700" };
    }
  };

  // Check if action should be disabled based on event state
  const isActionDisabled = (event, action) => {
    const eventState = event.event_state || event.status;
    if (!eventState) return false;
    
    const stateLower = eventState.toLowerCase();
    
    switch (action) {
      case "cancel":
        return stateLower === "cancelled" || stateLower === "closed";
      case "reschedule":
        return stateLower === "cancelled" || stateLower === "closed";
      case "edit":
        return stateLower === "cancelled" || stateLower === "closed";
      case "markAsClosed":
        return stateLower === "closed" || stateLower === "cancelled";
      default:
        return false;
    }
  };

  const getEventType = (event) => {
    if (!event.event_type || !Array.isArray(event.event_type)) return null;
    if (event.event_type.includes("conference")) return "conference";
    if (event.event_type.includes("trade_mission")) return "trade_mission";
    return null;
  };

  const getEventTypeIcon = (event) => {
    const type = getEventType(event);
    if (type === "conference") return GraduationCap;
    if (type === "trade_mission") return Briefcase;
    return null;
  };

  const getEventTypeColor = (event) => {
    const type = getEventType(event);
    if (type === "conference") return "bg-blue-100 text-blue-700";
    if (type === "trade_mission") return "bg-orange-100 text-orange-700";
    return "bg-gray-100 text-gray-700";
  };

  const getParticipantInfo = (event) => {
    if (event.total_participant_slots) {
      return {
        total: event.total_participant_slots,
        label: "Participants",
      };
    }
    return null;
  };

  if (events.length === 0) {
    return (
      <div className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
        <div className="py-16 text-center">
          <div className="inline-flex justify-center items-center mb-4 w-16 h-16 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full">
            <Calendar className="w-8 h-8 text-purple-600" />
          </div>
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            No conferences or trade missions found
          </h3>
          <p className="mx-auto max-w-sm text-sm text-gray-600">
            Try adjusting your filters to find what you're looking for, or create a new event.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-sm sm:rounded-2xl">
      {/* Table/Card Header */}
      <div className="px-4 py-4 bg-gradient-to-r from-purple-50 via-white to-purple-50 border-b border-gray-100 sm:px-6">
        <div className="flex justify-between items-center">
          <div>
            <h3 className="text-base font-bold text-gray-900 sm:text-lg">
              Conferences & Trade Missions
            </h3>
            <p className="mt-0.5 text-sm text-gray-600">
              {totalCount} {totalCount === 1 ? "event" : "events"} found
            </p>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <select
              value={itemsPerPage}
              onChange={(e) => onItemsPerPageChange(Number(e.target.value))}
              className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            >
              <option value={5}>5 per page</option>
              <option value={10}>10 per page</option>
              <option value={25}>25 per page</option>
              <option value={50}>50 per page</option>
            </select>
          </div>
        </div>
      </div>

      {/* Desktop Table View */}
      {viewMode === "table" && (
        <div className="hidden overflow-x-auto lg:block">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                  Event Info
                </th>
                <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                  Date & Time
                </th>
                <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                  Venue
                </th>
                <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                  Participants
                </th>
                <th className="px-6 py-4 text-xs font-bold tracking-wider text-left text-gray-700 uppercase">
                  Status
                </th>
                <th className="px-6 py-4 text-xs font-bold tracking-wider text-right text-gray-700 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {events.map((event) => {
                const status = getEventStatus(event);
                const eventType = getEventType(event);
                const TypeIcon = getEventTypeIcon(event);
                const participantInfo = getParticipantInfo(event);
                const organizerName = getOrganizerName(event);

                return (
                  <tr
                    key={event.slug || event.event_slug || event.id}
                    className="transition-colors duration-150 hover:bg-purple-50/30"
                  >
                    {/* Event Info */}
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-4">
                        {event.thumbnail && (
                          <div className="flex-shrink-0 overflow-hidden w-16 h-16 bg-gray-100 rounded-lg">
                            <img
                              src={event.thumbnail}
                              alt={getEventTitle(event)}
                              className="object-cover w-full h-full"
                              onError={(e) => {
                                e.target.style.display = "none";
                              }}
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <div className="text-sm font-bold text-gray-900 truncate whitespace-nowrap">
                              {getEventTitle(event)}
                            </div>
                            {TypeIcon && (
                              <TypeIcon className="flex-shrink-0 w-4 h-4 text-gray-400" />
                            )}
                          </div>
                          {organizerName && (
                            <div className="flex items-center mt-1 text-xs text-gray-600">
                              <Building2 className="w-3 h-3 mr-1.5 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{organizerName}</span>
                            </div>
                          )}
                          {eventType && (
                            <div className="mt-1">
                              <span
                                className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${getEventTypeColor(event)}`}
                              >
                                {eventType.replace("_", " ")}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>

                    {/* Date & Time */}
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center text-sm font-medium text-gray-900">
                          <Calendar className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0" />
                          {formatDate(event.event_date)}
                        </div>
                        {event.event_time && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0" />
                            {formatTime(event.event_time)}
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Venue */}
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="text-sm font-medium text-gray-900 truncate whitespace-nowrap">
                          {event.venue_name || "N/A"}
                        </div>
                        {(event.city || event.country) && (
                          <div className="flex items-center text-sm text-gray-600">
                            <MapPin className="w-3.5 h-3.5 mr-1 text-gray-400 flex-shrink-0" />
                            <span className="truncate">
                              {event.city}
                              {event.city && event.country && ", "}
                              {event.country}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Participants */}
                    <td className="px-6 py-4">
                      {participantInfo ? (
                        <div className="flex items-center text-sm text-gray-900">
                          <Users className="w-3.5 h-3.5 mr-2 text-gray-400 flex-shrink-0" />
                          <span className="font-medium">{participantInfo.total} slots</span>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">N/A</span>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex px-2.5 py-1 text-xs font-semibold rounded-full ${status.color}`}
                      >
                        {status.label}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="px-6 py-4">
                      <div className="flex justify-end items-center">
                        <EventActionsMenu
                          onViewDetails={() => onView(event)}
                          onViewAttendees={() => {
                            const eventType = getEventType(event);
                            if (eventType === "conference") {
                              navigate(`/admin/media/conferences/attendees`, {
                                state: { event: event }
                              });
                            } else if (eventType === "trade_mission") {
                              navigate(`/admin/media/trade-missions/attendees`, {
                                state: { event: event }
                              });
                            } else {
                              navigate(`/admin/media/conferences-trade-missions/attendees`, {
                                state: { event: event }
                              });
                            }
                          }}
                          onViewAnalytics={onViewAnalytics ? () => onViewAnalytics(event) : undefined}
                          onCancel={isActionDisabled(event, "cancel") ? undefined : () => handleCancel(event)}
                          onReschedule={isActionDisabled(event, "reschedule") ? undefined : () => handleReschedule(event)}
                          onEdit={isActionDisabled(event, "edit") ? undefined : () => onEdit(event)}
                          onDelete={() => handleDelete(event)}
                          onMarkAsClosed={isActionDisabled(event, "markAsClosed") ? undefined : () => handleMarkAsClosed(event)}
                          eventName={getEventTitle(event)}
                          align="right"
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Card View */}
      {viewMode === "card" && (
        <div className="grid grid-cols-1 gap-4 p-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {events.map((event) => {
            const status = getEventStatus(event);
            const eventType = getEventType(event);
            const TypeIcon = getEventTypeIcon(event);
            const participantInfo = getParticipantInfo(event);
            const organizerName = getOrganizerName(event);

            return (
              <div
                key={event.slug || event.event_slug || event.id}
                className="relative flex flex-col bg-white border border-gray-200 rounded-lg shadow-sm transition-all hover:shadow-lg hover:border-purple-300 hover:-translate-y-1"
              >
                {/* Event Image */}
                {event.thumbnail && (
                  <div className="relative overflow-hidden bg-gradient-to-br from-purple-100 to-purple-200 aspect-[16/10] rounded-t-lg">
                    <img
                      src={event.thumbnail}
                      alt={getEventTitle(event)}
                      className="object-cover w-full h-full"
                    />
                    <div className="absolute top-2 right-2 flex items-center space-x-2">
                      {TypeIcon && (
                        <div className="p-1.5 bg-white/90 rounded-full shadow-sm">
                          <TypeIcon className="w-4 h-4 text-purple-600" />
                        </div>
                      )}
                      <span
                        className="inline-flex px-2 py-0.5 text-xs font-semibold text-white rounded-full shadow-sm"
                        style={{
                          backgroundColor:
                            status.color.includes("green")
                              ? "rgba(34, 197, 94, 0.9)"
                              : status.color.includes("orange")
                              ? "rgba(249, 115, 22, 0.9)"
                              : status.color.includes("blue")
                              ? "rgba(37, 99, 235, 0.9)"
                              : status.color.includes("gray")
                              ? "rgba(107, 114, 128, 0.9)"
                              : "rgba(139, 92, 246, 0.9)",
                        }}
                      >
                        {status.label}
                      </span>
                    </div>
                  </div>
                )}

                {/* Event Content */}
                <div className="flex flex-col flex-1 p-4">
                  {/* Event Name & Type */}
                  <div className="mb-3">
                    <h3 className="mb-1.5 text-sm font-bold text-gray-900 truncate whitespace-nowrap">
                      {getEventTitle(event)}
                    </h3>
                    {organizerName && (
                      <div className="flex items-center mb-1.5 text-[10px] text-gray-600">
                        <Building2 className="w-3 h-3 mr-1 text-gray-400 flex-shrink-0" />
                        <span className="truncate">{organizerName}</span>
                      </div>
                    )}
                    {eventType && (
                      <span
                        className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${getEventTypeColor(event)}`}
                      >
                        {eventType.replace("_", " ")}
                      </span>
                    )}
                  </div>

                  {/* Event Details - Compact */}
                  <div className="mb-3 space-y-1.5">
                    <div className="flex items-center text-xs text-gray-600">
                      <Calendar className="w-3 h-3 mr-1.5 text-gray-400 flex-shrink-0" />
                      <span className="truncate">
                        {formatDate(event.event_date)}
                        {event.event_time && ` • ${formatTime(event.event_time)}`}
                      </span>
                    </div>
                    <div className="flex items-center text-xs text-gray-600">
                      <MapPin className="w-3 h-3 mr-1.5 text-gray-400 flex-shrink-0" />
                      <span className="truncate whitespace-nowrap">
                        {event.venue_name || "N/A"}
                        {(event.city || event.country) && " • "}
                        {event.city}
                        {event.city && event.country && ", "}
                        {event.country}
                      </span>
                    </div>
                  </div>

                  {/* Participant Info */}
                  {participantInfo && (
                    <div className="flex items-center justify-between px-2.5 py-2 mb-3 bg-gradient-to-r from-purple-50 to-blue-50 rounded text-xs">
                      <div className="flex items-center text-gray-700">
                        <Users className="w-3.5 h-3.5 mr-1.5 text-purple-600" />
                        <span className="font-semibold text-gray-900">{participantInfo.total}</span>
                        <span className="ml-1 text-gray-600">slots</span>
                      </div>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-3 mt-auto border-t border-gray-100">
                    <button
                      onClick={() => onView(event)}
                      className="flex items-center px-3 py-1.5 text-xs font-medium text-purple-600 bg-purple-50 rounded transition-colors hover:bg-purple-100"
                    >
                      <Eye className="mr-1 w-3 h-3" />
                      View
                    </button>
                    <EventActionsMenu
                      onViewDetails={() => onView(event)}
                      onViewAttendees={() => {
                        const eventType = getEventType(event);
                        if (eventType === "conference") {
                          navigate(`/admin/media/conferences/attendees`, {
                            state: { event: event }
                          });
                        } else if (eventType === "trade_mission") {
                          navigate(`/admin/media/trade-missions/attendees`, {
                            state: { event: event }
                          });
                        } else {
                          navigate(`/admin/media/conferences-trade-missions/attendees`, {
                            state: { event: event }
                          });
                        }
                      }}
                      onViewAnalytics={onViewAnalytics ? () => onViewAnalytics(event) : undefined}
                      onCancel={isActionDisabled(event, "cancel") ? undefined : () => handleCancel(event)}
                      onReschedule={isActionDisabled(event, "reschedule") ? undefined : () => handleReschedule(event)}
                      onEdit={isActionDisabled(event, "edit") ? undefined : () => onEdit(event)}
                      onDelete={() => handleDelete(event)}
                      onMarkAsClosed={isActionDisabled(event, "markAsClosed") ? undefined : () => handleMarkAsClosed(event)}
                      eventName={getEventTitle(event)}
                      align="right"
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Mobile Card View (for small screens when table mode is selected) */}
      {viewMode === "table" && (
        <div className="lg:hidden">
          <div className="divide-y divide-gray-200">
            {events.map((event) => {
              const status = getEventStatus(event);
              const eventType = getEventType(event);
              const TypeIcon = getEventTypeIcon(event);
              const participantInfo = getParticipantInfo(event);
              const organizerName = getOrganizerName(event);

              return (
                <div
                  key={event.slug || event.event_slug || event.id}
                  className="p-4 transition-colors hover:bg-gray-50"
                >
                  <div className="flex items-start justify-between space-x-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start space-x-3">
                        {event.thumbnail && (
                          <div className="flex-shrink-0 overflow-hidden w-16 h-16 bg-gray-100 rounded-lg">
                            <img
                              src={event.thumbnail}
                              alt={getEventTitle(event)}
                              className="object-cover w-full h-full"
                            />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-bold text-gray-900 truncate whitespace-nowrap">
                              {getEventTitle(event)}
                            </h4>
                            {TypeIcon && (
                              <TypeIcon className="flex-shrink-0 w-4 h-4 text-gray-400" />
                            )}
                          </div>
                          {organizerName && (
                            <div className="flex items-center mt-1 text-xs text-gray-600">
                              <Building2 className="w-3 h-3 mr-1.5 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{organizerName}</span>
                            </div>
                          )}
                          {eventType && (
                            <div className="mt-1">
                              <span
                                className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${getEventTypeColor(event)}`}
                              >
                                {eventType.replace("_", " ")}
                              </span>
                            </div>
                          )}
                          <div className="mt-2 space-y-1.5">
                            <div className="flex items-center text-xs text-gray-600">
                              <Calendar className="w-3 h-3 mr-1.5 text-gray-400" />
                              {formatDate(event.event_date)}
                              {event.event_time && ` • ${formatTime(event.event_time)}`}
                            </div>
                            <div className="flex items-center text-xs text-gray-600">
                              <MapPin className="w-3 h-3 mr-1.5 text-gray-400 flex-shrink-0" />
                              <span className="truncate whitespace-nowrap">{event.venue_name || "N/A"}</span>
                            </div>
                            {participantInfo && (
                              <div className="flex items-center text-xs text-gray-600">
                                <Users className="w-3 h-3 mr-1.5 text-gray-400" />
                                {participantInfo.total} participant slots
                              </div>
                            )}
                          </div>
                          <div className="mt-2">
                            <span
                              className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${status.color}`}
                            >
                              {status.label}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <EventActionsMenu
                        onViewDetails={() => onView(event)}
                        onViewAttendees={() => {
                          const eventType = getEventType(event);
                          if (eventType === "conference") {
                            navigate(`/admin/media/conferences/attendees`, {
                              state: { event: event }
                            });
                          } else if (eventType === "trade_mission") {
                            navigate(`/admin/media/trade-missions/attendees`, {
                              state: { event: event }
                            });
                          } else {
                            navigate(`/admin/media/conferences-trade-missions/attendees`, {
                              state: { event: event }
                            });
                          }
                        }}
                        onViewAnalytics={onViewAnalytics ? () => onViewAnalytics(event) : undefined}
                        onCancel={isActionDisabled(event, "cancel") ? undefined : () => handleCancel(event)}
                        onReschedule={isActionDisabled(event, "reschedule") ? undefined : () => handleReschedule(event)}
                        onEdit={isActionDisabled(event, "edit") ? undefined : () => onEdit(event)}
                        onDelete={() => handleDelete(event)}
                        onMarkAsClosed={isActionDisabled(event, "markAsClosed") ? undefined : () => handleMarkAsClosed(event)}
                        eventName={getEventTitle(event)}
                        align="right"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {totalPages > 1 && (
        <div className="px-4 py-4 bg-gray-50 border-t border-gray-100 sm:px-6">
          <div className="flex flex-col justify-between items-center space-y-4 sm:flex-row sm:space-y-0">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex}</span> to{" "}
              <span className="font-medium">{endIndex}</span> of{" "}
              <span className="font-medium">{totalCount}</span> results
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="p-2 text-gray-400 bg-white border border-gray-300 rounded-lg transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => onPageChange(pageNum)}
                      className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                        currentPage === pageNum
                          ? "text-white bg-purple-600"
                          : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="p-2 text-gray-400 bg-white border border-gray-300 rounded-lg transition-colors hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Event Modal */}
      {eventToReschedule && (
        <RescheduleEventModal
          isOpen={isRescheduleModalOpen}
          onClose={() => {
            setIsRescheduleModalOpen(false);
            setEventToReschedule(null);
          }}
          onConfirm={handleRescheduleConfirm}
          currentDate={formatDateForInput(eventToReschedule.event_date)}
          currentTime={eventToReschedule.event_time || ""}
          eventName={eventToReschedule.event_title || eventToReschedule.venue_name}
          isLoading={isRescheduling}
        />
      )}

      {/* Cancel Event Modal */}
      {eventToCancel && (
        <CancelEventModal
          isOpen={isCancelModalOpen}
          onClose={() => {
            setIsCancelModalOpen(false);
            setEventToCancel(null);
          }}
          onConfirm={handleCancelConfirm}
          eventName={eventToCancel.event_title || eventToCancel.venue_name}
          isLoading={isCancelling}
        />
      )}

      {/* Delete Event Modal */}
      {eventToDelete && (
        <DeleteEventModal
          isOpen={isDeleteModalOpen}
          onClose={() => {
            setIsDeleteModalOpen(false);
            setEventToDelete(null);
          }}
          onConfirm={handleDeleteConfirm}
          eventName={eventToDelete.event_title || eventToDelete.venue_name}
          isLoading={isDeleting}
        />
      )}

      {/* Mark as Closed Event Modal */}
      {eventToClose && (
        <MarkAsClosedModal
          isOpen={isMarkAsClosedModalOpen}
          onClose={() => {
            setIsMarkAsClosedModalOpen(false);
            setEventToClose(null);
          }}
          onConfirm={handleMarkAsClosedConfirm}
          eventName={eventToClose.event_title || eventToClose.venue_name}
          isLoading={isClosing}
        />
      )}
    </div>
  );
};

export default ConferenceTradeMissionTable;

